import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { ShareService } from '../../services/share.service';
import { PlanogramService } from '../../services/planogram.service';
import { ToastService } from '../../services/toasts.service';
import { DialogService } from 'src/app/services/dialog.service';
import { PlanogramSharedUserDTO, UserDTO } from 'src/app/models/user';
import { ShareType } from 'src/app/models/share-type';
import { ShareSettings } from 'src/app/models/share-settings';
import { NgFor, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-share-planogram-modal',
    templateUrl: './share-planogram-modal.component.html',
    styleUrl: './share-planogram-modal.component.scss',
    imports: [
        NgFor,
        FormsModule,
        DatePipe,
    ],
})
export class SharePlanogramModalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  sharedUsers: PlanogramSharedUserDTO[] = [];
  newUser: Partial<UserDTO> & { shareType?: ShareType } = {};
  shareSettings: ShareSettings | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private shareService: ShareService,
    private planogramService: PlanogramService,
    private dialogService: DialogService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    const planogramId = this.planogramService.getPlanogram();
    if (!planogramId) {
      this.toastService.show('No planogram loaded.', {
        classname: `bg-warning text-dark p-3 fs-5`,
        delay: 3000,
      });
      return;
    }

    this.shareService
      .getPlanogramShareSettings(planogramId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          if (settings) {
            this.shareSettings = settings;
            this.sharedUsers = settings.users;
          }
        },
        error: (error) => {
          console.error('Failed to load share settings:', error);
          this.toastService.show('Failed to load share settings.', {
            classname: `bg-danger text-light p-3 fs-5`,
            delay: 5000,
          });
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addUser(): void {
    const { name, surname, email, shareType } = this.newUser;
    if (!name || !surname || !email || shareType === undefined) return;

    const planogramId = this.planogramService.getPlanogram();
    const stateId = this.planogramService.getPlanogramState();
    if (!planogramId || !stateId) return;

    const user: UserDTO = { name, surname, email };
    this.dialogService.showLoading('Adding user...');
    this.shareService
      .addSharedUser(planogramId, stateId, user, shareType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (newUser) => {
          this.dialogService.closeLoading();
          if (newUser) {
            this.toastService.show('User added successfully.', {
              classname: `bg-success text-light p-3 fs-5`,
              delay: 3000,
            });
            this.sharedUsers.push(newUser);
            this.newUser = {};
          }
        },
        error: (err) => {
          this.dialogService.closeLoading();
          console.error('Error adding user:', err);
          this.toastService.show('Failed to add shared user.', {
            classname: `bg-danger text-light p-3 fs-5`,
            delay: 5000,
          });
        },
      });
  }

  removeUser(user: PlanogramSharedUserDTO): void {
  const planogramId = this.planogramService.getPlanogram();
  if (!planogramId || !user.id) return;

  this.dialogService
    .showConfirmation(
      'Confirm Removal',
      `Are you sure you want to remove <b>${user.email}</b> from the shared list?`
    )
    .then((confirmed) => {
      if (!confirmed) return;

      this.dialogService.showLoading('Removing user...');
      this.shareService
        .removeSharedUser(planogramId, user.id!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (success) => {
            this.dialogService.closeLoading();
            if (success) {
              this.sharedUsers = this.sharedUsers.filter((u) => u.id !== user.id);
              this.toastService.show('User removed successfully.', {
                classname: `bg-success text-light p-3 fs-5`,
                delay: 3000,
              });
            }
          },
          error: (err) => {
            this.dialogService.closeLoading();
            console.error('Error removing user:', err);
            this.toastService.show('Failed to remove user.', {
              classname: `bg-danger text-light p-3 fs-5`,
              delay: 5000,
            });
          },
        });
    });
}

  updateAccess(user: PlanogramSharedUserDTO, newType: ShareType): void {
    const planogramId = this.planogramService.getPlanogram();
    const stateId = this.planogramService.getPlanogramState();
    if (!planogramId || !stateId || !user.id) return;

    this.dialogService.showLoading('Updating user access...');
    this.shareService
      .updateUserShareType(planogramId, stateId, user.id, newType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (success) => {
          this.dialogService.closeLoading();
          if (success) {
            user.shareType = newType;
            this.toastService.show('User access updated successfully.', {
              classname: `bg-success text-light p-3 fs-5`,
              delay: 3000,
            });
          }
        },
        error: (err) => {
          this.dialogService.closeLoading();
          console.error('Error updating user share type:', err);
          this.toastService.show('Failed to update user access.', {
            classname: `bg-danger text-light p-3 fs-5`,
            delay: 5000,
          });
        },
      });
  }

  toggleCollaboration(): void {
    if (this.shareSettings) {
      this.shareSettings.collaborationEnabled = !this.shareSettings.collaborationEnabled;
      this.saveSettings();
    }
  }

  setDeadline(eventTarget: EventTarget | null): void {
  if (this.shareSettings) {
    const input = eventTarget as HTMLInputElement;
    const value = input?.value ?? '';

    this.shareSettings.feedbackDeadline = value.length > 0
      ? new Date(value).toISOString() // store as ISO datetime string
      : null;

    this.saveSettings();
  }
}

  clearDeadline(): void {
    if (this.shareSettings) {
      this.shareSettings.feedbackDeadline = null;
      this.saveSettings();
    }
  }

  private saveSettings(): void {
    const planogramId = this.planogramService.getPlanogram();
    if (!planogramId || !this.shareSettings) return;

    this.shareService
      .updateShareSettings(planogramId, this.shareSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show('Share settings updated.', {
            classname: `bg-success text-light p-3 fs-5`,
            delay: 2000,
          });
        },
        error: (err) => {
          console.error('Error updating settings:', err);
          this.toastService.show('Failed to update settings.', {
            classname: `bg-danger text-light p-3 fs-5`,
            delay: 4000,
          });
        },
      });
  }

  synchronize(): void {
    this.dialogService
      .showWarningConfirmation(
        'Synchronize shared planograms?',
        'This action will revert all shared users to the current state of the planogram.\r\n' +
        'All external versions will be updated to match this one.\r\n\r\nProceed only after incorporating\r\n' +
        'feedback and finalizing changes for the next review round.'
      )
      .then((confirmed) => {
        if (!confirmed) return;

        const planogramId = this.planogramService.getPlanogram();
        const stateId = this.planogramService.getPlanogramState();
        if (!planogramId || !stateId) {
          this.toastService.show('No planogram to synchronize.', {
            classname: `bg-danger text-light p-3 fs-5`,
            delay: 3000,
          });
          return;
        }

        this.dialogService.showLoading('Synchronizing shared planograms...');
        this.shareService
          .synchronizePlanogram(planogramId, stateId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (success) => {
              this.dialogService.closeLoading();
              if (success) {
                this.toastService.show(
                  'Shared planograms synchronized successfully.',
                  { classname: `bg-success text-light p-3 fs-5`, delay: 3000 }
                );
                this.activeModal.close();
              } else {
                this.toastService.show('Failed to synchronize planograms.', {
                  classname: `bg-danger text-light p-3 fs-5`,
                  delay: 5000,
                });
              }
            },
            error: (error) => {
              this.dialogService.closeLoading();
              console.error('Error synchronizing planogram:', error);
              this.toastService.show('Error synchronizing planogram.', {
                classname: `bg-danger text-light p-3 fs-5`,
                delay: 5000,
              });
            },
          });
      });
  }

  cancel(): void {
    this.activeModal.close(null);
  }
}